import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { resetData } from "../../features/product";
import { downloadPdf } from "../../utils/commonHelper";
import { fetchAndDownloadTakafulPDF } from "../../utils/takafulHelpers";
import { pdfFileName } from "../../config/constants";
import { ReactComponent as OmrSymbol } from "../../assets/SVG/omr_symbol.svg";
import "./style.css";

const PolicyDownload = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { policyData } = useSelector((state) => state.product);
  const isRTL = i18n.language === "ar";

  const policyInfo = location.state || {};
  const isEbridge = policyInfo?.is_ebridge === true;
  const isAttention =
    !isEbridge && (policyData?.nstp_final === "Yes" || policyData?.inspect_flag === "Yes");

  const handleDownload = () => {
    if (!policyData) return;

    if (
      policyData?.pdfContent?.document &&
      policyData?.pdfContent?.document?.content !== ""
    ) {
      downloadPdf(policyData.pdfContent.document).then(() => {
        dispatch(resetData());
        navigate("/");
      });
    } else if (policyData?.pdfContent && policyData?.pdfContent !== "") {
      const urlRegex = /(https?:\/\/[^\s]+)/g;

      if (policyData.pdfContent?.includes("download=1")) {
        fetchAndDownloadTakafulPDF(
          policyData.pdfContent,
          `${policyData?.plan_type?.replace(/\s+/g, "_")}-${policyData?.policy_id}.pdf`,
          policyData?.token
        );
        dispatch(resetData());
        navigate("/");
      } else if (urlRegex.test(policyData.pdfContent)) {
        dispatch(resetData());
        window.open(policyData.pdfContent, "_blank");
        navigate("/");
      } else {
        let product_detail = localStorage.getItem("product_details")
          ? JSON.parse(localStorage.getItem("product_details"))
          : {};
        let file_name =
          pdfFileName?.[product_detail?.product_id]?.[policyData?.company_id] ||
          "Insurance Policy";
        downloadPdf({
          name: file_name,
          mimeType: "application/pdf",
          content: policyData.pdfContent,
        }).then(() => {
          dispatch(resetData());
          navigate("/");
        });
      }
    }
  };

  const handleGoHome = () => {
    dispatch(resetData());
    navigate("/");
  };

  return (
    <section className="policy-download-page" dir={isRTL ? "rtl" : "ltr"}>
      <div className="policy-download-card">
        <div
          className={`policy-success-icon ${isAttention ? "policy-attention-icon" : ""}`}
        >
          {isAttention ? "!" : "\u2713"}
        </div>

        <h2>
          {isAttention ? t("attention") : t("payment_successful")}
        </h2>

        <p className="policy-subtitle">
          {isEbridge
            ? "Contact admin to receive your policy."
            : isAttention
            ? t("policy_manual_verification")
            : t("policy_download_description")}
        </p>

        <div className="policy-details-section">
          {(() => {
            const pData = Array.isArray(policyData) ? policyData[0] : policyData;
            const fData = pData?.data || pData?.api_response || pData?.result || pData?.body || pData;
            const dData = Array.isArray(fData) ? fData[0] : fData;
            const pNumber = pData?.policy_id || pData?.policy_number || pData?.policy_no || pData?.policyNumber || pData?.PolicyNumber || pData?.PolicyNo || dData?.policy_number || dData?.policy_id || dData?.CertNo || dData?.CertNumber || dData?.certificateNo || dData?.CertificateNo || dData?.certificate_number || dData?.cert_number || dData?.DocumentNo || dData?.DocumentNumber || pData?.policy?.policy_number || pData?.policy?.policy_id || pData?.policy_ref || pData?.policyRef || pData?.policy_reference || pData?.policyReference || dData?.policy_ref || dData?.policyRef || dData?.policy_reference || dData?.policy_reference || pData?.api_response?.policy_number || pData?.api_response?.policy_id || pData?.policy_data?.policy_number || pData?.policy_data?.policy_id || dData?.Policy_Num || dData?.Policy_No || dData?.Pol_No || dData?.PolNo || dData?.policyNo || dData?.policy_ID;
            return pNumber && (
              <div className="policy-detail-row">
                <span className="policy-detail-label">
                  {t("policy_number")}
                </span>
                <span className="policy-detail-value">
                  {pNumber}
                </span>
              </div>
            );
          })()}

          {policyData?.plan_type && (
            <div className="policy-detail-row">
              <span className="policy-detail-label">
                {t("plan_type")}
              </span>
              <span className="policy-detail-value">
                {policyData.plan_type}
              </span>
            </div>
          )}

          {policyInfo?.company_name && (
            <div className="policy-detail-row">
              <span className="policy-detail-label">
                {t("insurance_company")}
              </span>
              <span className="policy-detail-value">
                {policyInfo.company_name}
              </span>
            </div>
          )}

          {(policyInfo?.premium_value || policyData?.premium_value) && (
            <div className="policy-detail-row">
              <span className="policy-detail-label">
                {t("premium_amount")}
              </span>
              <span className="policy-detail-value">
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  {String(
                    policyInfo?.premium_currency ||
                    policyData?.premium_currency ||
                    "OMR"
                  )
                    .trim()
                    .toUpperCase() === "OMR" ? (
                    <OmrSymbol width={16} height={16} />
                  ) : (
                    policyInfo?.premium_currency ||
                    policyData?.premium_currency ||
                    "OMR"
                  )}
                  {parseFloat(
                    policyInfo?.premium_value ||
                    policyData?.premium_value ||
                    0
                  ).toFixed(3)}
                </span>
              </span>
            </div>
          )}
        </div>

        <div className="policy-btn-group">
          {!isEbridge && !isAttention && policyData?.pdfContent && (
            <button className="policy-btn-download" onClick={handleDownload}>
              {t("download")} {t("policyDocument")}
            </button>
          )}

          <button className="policy-btn-home" onClick={handleGoHome}>
            {t("go_to_home")}
          </button>
        </div>
      </div>
    </section>
  );
};

export default PolicyDownload;
